// Dialog popover anchored to a cluster bubble. Lists each milestone with its
// status pill and slip/date suffix. Focus-trap, Escape dismiss, focus returns
// to the triggering bubble. No currency surface — keeps QITC payment-not-
// completion guardrail clean.

import { useCallback, useEffect, useRef } from "react";
import { clsx } from "clsx";
import { X } from "lucide-react";
import type { Milestone } from "../../../../types";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import {
  deriveMilestoneDisplayStatus,
  type MilestoneDisplayStatusResult,
} from "../../../../utils/deriveMilestoneDisplayStatus";
import {
  deriveMilestoneDisplayLifecycle,
  deriveTimelineDotLifecycle,
} from "../../../../utils/deriveMilestoneDisplayLifecycle";
import { formatDayMonth } from "../../../../utils/formatShortDate";
import { dotClass } from "./timelineDotClass";
import { daysBetween, effectiveTimelineDate } from "./timelineGeometry";

function suffixFor(
  m: Milestone,
  display: MilestoneDisplayStatusResult,
  today: string,
): string {
  if (display.riskOverlay === "late" && m.plannedDate) {
    const days = Math.max(0, daysBetween(m.plannedDate, today));
    return `▲ ${days}d late`;
  }
  if (display.riskOverlay === "delivered-late" && m.plannedDate && m.deliveredAt) {
    const days = Math.max(0, daysBetween(m.plannedDate, m.deliveredAt));
    return `▲ delivered ${days}d late`;
  }
  if (display.lifecycle === "paid" && m.paidAt) {
    return `Paid ${formatDayMonth(m.paidAt)}`;
  }
  if (m.plannedDate) {
    return `Due ${formatDayMonth(m.plannedDate)}`;
  }
  return "";
}

function formatDateRange(milestones: Milestone[]): string {
  // Match the timeline-dot rule and the cluster-bubble tooltip: range is
  // built from the date the dot is plotted against, so the popover header
  // and the bubble agree on what "this cluster" covers.
  const dates = milestones
    .map(effectiveTimelineDate)
    .filter((d): d is string => typeof d === "string")
    .sort();
  if (dates.length === 0) return "";
  if (dates[0] === dates[dates.length - 1]) {
    return formatDayMonth(dates[0]);
  }
  return `${formatDayMonth(dates[0])} – ${formatDayMonth(dates[dates.length - 1])}`;
}

export interface ClusterPopoverProps {
  open: boolean;
  milestones: Milestone[];
  today: string;
  onActivate: (milestoneId: string) => void;
  onClose: () => void;
}

export default function ClusterPopover({
  open,
  milestones,
  today,
  onActivate,
  onClose,
}: ClusterPopoverProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const firstRowRef = useRef<HTMLButtonElement | null>(null);

  // Focus first row on open; Escape dismisses; trap focus within the dialog.
  useEffect(() => {
    if (!open) return;
    firstRowRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusables = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("disabled"));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const handleRow = useCallback(
    (id: string) => {
      onActivate(id);
      onClose();
    },
    [onActivate, onClose],
  );

  if (!open) return null;

  const dateRange = formatDateRange(milestones);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-label={`Cluster of ${milestones.length} milestones`}
      data-testid="cluster-popover"
      className={clsx(
        "absolute z-20 w-80 max-w-[calc(100vw-2rem)] rounded-md border border-slate-200 bg-white p-3 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800",
        // Anchor: the parent <div> (active-window-cluster-anchor-*) sits at
        // the cluster's fractional position on the axis line. Centering the
        // popover horizontally on that anchor (left-0 + -translate-x-1/2)
        // and pushing it down past the bubble (top-7 ≈ 28px > bubble half-
        // height of 20px + 8px gap) puts the popover directly under the
        // bubble that opened it.
        "left-0 top-7 -translate-x-1/2",
        // Bottom-sheet treatment on narrow viewports.
        "max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:left-0 max-sm:top-auto max-sm:translate-x-0 max-sm:w-full max-sm:rounded-b-none",
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div
          data-testid="cluster-popover-header"
          className="text-caption text-slate-500 dark:text-slate-400"
        >
          {milestones.length} milestones{dateRange ? ` · ${dateRange}` : ""}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close cluster"
          data-testid="cluster-popover-close"
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <ul className="space-y-1">
        {milestones.map((m, i) => {
          const display = deriveMilestoneDisplayStatus(m, today);
          const suffix = suffixFor(m, display, today);
          // Mirror MilestoneTab's status-column construction: badge uses
          // the display lifecycle ("partially-accepted" survives partial+
          // paid carve-out), with a secondaryPill: "paid" tagged so the
          // badge renders both [Partially accepted, Paid] pills. The dot
          // separately uses the timeline lifecycle, which collapses to
          // "partial-paid" for the same row so the gradient renders.
          // Without these the popover row claimed the milestone was simply
          // "Paid" and dropped the still-actionable partial-accept signal.
          const badgeLifecycle = deriveMilestoneDisplayLifecycle(m);
          const dotLifecycle = deriveTimelineDotLifecycle(m);
          const isPartialPaid =
            badgeLifecycle === "partially-accepted" && Boolean(m.paidAt);
          const badgeStatus = {
            ...display,
            lifecycle: badgeLifecycle,
            ...(isPartialPaid ? { secondaryPill: "paid" as const } : {}),
          };
          return (
            <li key={m.id}>
              <button
                ref={i === 0 ? firstRowRef : undefined}
                type="button"
                data-testid={`cluster-popover-row-${m.id}`}
                onClick={() => handleRow(m.id)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-slate-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:hover:bg-slate-700/40"
              >
                <span
                  aria-hidden="true"
                  data-testid={`cluster-popover-dot-${m.id}`}
                  className={clsx(
                    "h-2.5 w-2.5 flex-none rounded-full",
                    // Use the lifecycle-overridden dot class so the bullet
                    // matches the badge — partial+paid renders the split
                    // gradient on both surfaces.
                    dotClass({ ...display, lifecycle: dotLifecycle }),
                  )}
                />
                <span className="text-caption font-semibold text-slate-700 dark:text-slate-200">
                  #{m.milestoneNumber}
                  {m.milestoneSuffix ?? ""}
                </span>
                <span className="flex-1 truncate text-body-sm text-slate-900 dark:text-slate-100">
                  {m.name}
                </span>
                <TrackingStatusBadge kind="milestone" status={badgeStatus} />
                {suffix && (
                  <span className="text-caption text-slate-500 dark:text-slate-400">
                    {suffix}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
